// Event categories (DESIGN_SPEC §9, §12.3): fixed legend order, one shape each, colour only
// confirms the shape. Unknown kinds are system.
//   CATEGORIES                 legend order: process file registry network memory detection system
//   CATEGORY[c]                { label, shape, kinds, what, source } ("What a run records" rows)
//   categoryOf(kind)           kind → category

//   emptyCounts() · countByCategory(events)
import type { Event } from "@/types";
import type { CatShape, Category } from "./types";

export const CATEGORIES: readonly Category[] = ["process", "file", "registry", "network", "memory", "detection", "system"];

type CategoryInfo = {
  label: string;
  shape: CatShape;
  kinds: readonly string[];
  /** What the category records, for the Submit legend ("starts, exits, image loads"). */
  what: string;
  /** Which sensor produces it. */
  source: string;
};

export const CATEGORY: Readonly<Record<Category, CategoryInfo>> = {
  process: {
    label: "process",
    shape: "square",
    kinds: ["proc.start", "proc.exit", "image.load"],
    what: "starts, exits, image loads",
    source: "guest driver",
  },
  file: {
    label: "file",
    shape: "rect",
    kinds: ["file.create", "file.write", "file.delete", "file.rename", "file.drop"],
    what: "creates, writes, renames, deletes",
    source: "guest driver",
  },
  registry: {
    label: "registry",
    shape: "diamond",
    kinds: ["registry.set", "registry.delete"],
    what: "values set and deleted",
    source: "guest driver",
  },
  network: {
    label: "network",
    shape: "dot",
    kinds: ["net.dns", "net.flow", "net.http", "net.conn"],
    what: "DNS, connections, flows, HTTP through the TLS proxy",
    source: "host capture, proxy and guest",
  },
  memory: {
    label: "memory",
    shape: "triangle",
    kinds: ["thread.inject", "mutex.create"],
    what: "threads written into other processes, mutexes",
    source: "guest driver",
  },
  detection: {
    label: "detection",
    shape: "asterisk",
    kinds: ["yara.match", "config.extracted", "dump.taken", "verdict"],
    what: "YARA matches, extracted malware configs, process dumps, the verdict",
    source: "host analyzer",
  },
  system: {
    label: "system",
    shape: "ring",
    kinds: ["job.state", "log", "agent.hello", "agent.lost", "artifact", "screenshot"],
    what: "lifecycle, logs, guest agent, artifacts, screenshots",
    source: "host",
  },
};

const KIND_TO_CATEGORY: ReadonlyMap<string, Category> = new Map(
  CATEGORIES.flatMap((c) => CATEGORY[c].kinds.map((k) => [k, c] as const)),
);

export function categoryOf(kind: string): Category {
  const known = KIND_TO_CATEGORY.get(kind);
  if (known) return known;
  // Future kinds in a known family keep their family; anything else is system.
  if (kind.startsWith("proc.")) return "process";
  if (kind.startsWith("file.")) return "file";
  if (kind.startsWith("registry.")) return "registry";
  if (kind.startsWith("net.")) return "network";
  return "system";
}


export function emptyCounts(): Record<Category, number> {
  return { process: 0, file: 0, registry: 0, network: 0, memory: 0, detection: 0, system: 0 };
}

export function countByCategory(events: readonly Pick<Event, "kind">[]): Record<Category, number> {
  const c = emptyCounts();
  for (const e of events) c[categoryOf(e.kind)]++;
  return c;
}
